import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuX } from 'react-icons/lu';
import BookProgressIcon from './BookProgressIcon.tsx';
import { InlineLinkButton } from '@/components/ui/inline-link-button';
import {
	KNOWLEDGE_BASE_READINESS_COPY,
	KnowledgeBaseReadinessState,
	type KnowledgeBaseReadinessSummary,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';

interface KnowledgeBaseBannerViewProps {
	onDismiss: () => void;
	onViewRecords?: () => void;
	progressValue: number;
	state: KnowledgeBaseReadinessStateValue;
	variant?: 'dashboard' | 'records';
	summary?: KnowledgeBaseReadinessSummary | null;
}

/** Banner body text steps down on small screens where the progress ring + close button crowd the row. */
const BANNER_TEXT_SIZE = { base: 'sm', md: 'md' } as const;

const recordsLabel = (count: number): string => `${count} ${count === 1 ? 'record' : 'records'}`;

const documentsLabel = (count: number): string => `${count} ${count === 1 ? 'document' : 'documents'}`;

/**
 * The two reading states say the same thing at different volumes, so they read the same way.
 *
 * Zero means nothing countable is left rather than nothing at all: the caller derives this summary
 * over documents alone, because every important system lives in one record that no list on this
 * page accounts for. The banner stays up on the strength of its title, which already says reading
 * is going on — disappearing would leave the assistant answering from a half-read property with
 * nothing on screen saying so.
 *
 * What it must not do is restate that title. "Reading new records. Reading your property
 * information." says one thing twice, so with nothing to count the line carries the only part the
 * heading does not: that there is nothing to do about it.
 */
const inProgressDetail = (documents: number): string =>
	documents > 0 ? `${documentsLabel(documents)} in progress.` : NO_ACTION_NEEDED;

/** Delay is not a fault, so the sentence that says so is part of every version of this line. */
const NO_ACTION_NEEDED = 'No action needed.';
const DELAYED_REASSURANCE = 'No action needed - processing will continue in the background.';

const delayedDetail = (documents: number): string =>
	documents > 0 ? `${documentsLabel(documents)} still being processed. ${DELAYED_REASSURANCE}` : DELAYED_REASSURANCE;

const getRecordsCopy = (state: KnowledgeBaseReadinessStateValue, summary?: KnowledgeBaseReadinessSummary | null) => {
	const reading = summary?.reading ?? 0;
	const stalled = summary?.stalled ?? 0;
	const failed = summary?.failed ?? 0;

	switch (state) {
		case KnowledgeBaseReadinessState.Processing:
			return { title: 'Reading new records.', detail: inProgressDetail(reading) };
		case KnowledgeBaseReadinessState.Delayed:
			return {
				title: 'Still getting your records ready.',
				detail: delayedDetail(reading),
			};
		case KnowledgeBaseReadinessState.Mixed: {
			const mixedDetails = [
				reading > 0 ? `${reading} reading` : null,
				stalled > 0 ? `${stalled} stalled` : null,
				failed > 0 ? `${failed} flagged for review` : null,
			].filter((detail): detail is string => detail !== null);
			return {
				title: stalled > 0 ? 'Some processing has stalled.' : 'Some records are still processing.',
				detail: `${mixedDetails.join(' · ')}.`,
			};
		}
		case KnowledgeBaseReadinessState.Failed:
			return {
				title: 'Some records could not be processed.',
				detail: `${recordsLabel(failed)} flagged for our review.`,
			};
		case KnowledgeBaseReadinessState.Stalled:
			return {
				title: `${stalled} ${stalled === 1 ? 'file' : 'files'} stalled.`,
				detail: `Please delete and re-upload ${stalled === 1 ? 'it' : 'them'} to retry.`,
			};
	}
};

/**
 * Pure visual banner, no logic, only rendering.
 */
const KnowledgeBaseBannerView: React.FC<KnowledgeBaseBannerViewProps> = (props) => {
	const { onDismiss, onViewRecords, progressValue, state, variant = 'dashboard', summary } = props;
	const isRecordsVariant = variant === 'records';
	const showViewRecords = !isRecordsVariant && onViewRecords;
	const terminalFailure = state === KnowledgeBaseReadinessState.Failed;
	const dashboardCopy = KNOWLEDGE_BASE_READINESS_COPY[state];
	const {
		bannerTitle: title,
		bannerMobileTitle: mobileTitle,
		bannerDetail: detail,
		bannerMobileDetail: mobileDetail,
	} = dashboardCopy;
	const recordsCopy = variant === 'records' ? getRecordsCopy(state, summary) : null;
	const resolvedTitle = recordsCopy?.title ?? title;
	const resolvedMobileTitle = recordsCopy?.title ?? mobileTitle;
	const resolvedDetail = recordsCopy?.detail ?? detail;
	const resolvedMobileDetail = recordsCopy?.detail ?? mobileDetail;

	return (
		<Box
			display="grid"
			gridTemplateColumns={
				isRecordsVariant
					? { base: 'auto minmax(0, 1fr) auto', md: 'auto auto minmax(0, 1fr) auto' }
					: 'auto minmax(0, 1fr) auto'
			}
			columnGap={{ base: 2, md: 3 }}
			rowGap={{ base: 1, md: 0 }}
			alignItems="center"
			w="100%"
			minH={isRecordsVariant ? 'auto' : { base: 'auto', md: '96px' }}
			px={isRecordsVariant ? 3 : { base: 3, md: 4 }}
			py={isRecordsVariant ? 3 : { base: 3, md: 4 }}
			bg="blue.50"
			borderRadius="3xl"
			boxShadow="sm"
		>
			<Box
				gridColumn={1}
				gridRow={isRecordsVariant ? { base: '1 / span 2', md: 1 } : { base: 1, md: '1 / span 2' }}
				alignSelf={isRecordsVariant ? 'center' : { base: 'start', md: 'center' }}
			>
				<BookProgressIcon value={terminalFailure ? 100 : progressValue} state={state} />
			</Box>
			<Text
				gridColumn={2}
				gridRow={1}
				color="brand.fg"
				fontSize={BANNER_TEXT_SIZE}
				fontWeight="semibold"
				lineHeight="tall"
			>
				<Box as="span" display={{ base: 'inline', md: 'none' }}>
					{resolvedMobileTitle}
				</Box>
				<Box as="span" display={{ base: 'none', md: 'inline' }}>
					{resolvedTitle}
				</Box>
			</Text>
			<IconButton
				gridColumn={isRecordsVariant ? { base: 3, md: 4 } : 3}
				gridRow={isRecordsVariant ? { base: '1 / span 2', md: 1 } : { base: 1, md: '1 / span 2' }}
				aria-label="Dismiss banner"
				variant="ghost"
				size="sm"
				_hover={{ bg: 'white' }}
				onClick={onDismiss}
				flexShrink={0}
				alignSelf={isRecordsVariant ? 'center' : { base: 'start', md: 'center' }}
			>
				<LuX />
			</IconButton>
			<Text
				gridColumn={isRecordsVariant ? { base: 2, md: 3 } : { base: '1 / -1', md: 2 }}
				gridRow={isRecordsVariant ? { base: 2, md: 1 } : 2}
				alignSelf="center"
				color="brand.fg"
				fontSize={BANNER_TEXT_SIZE}
				fontWeight="normal"
				lineHeight="tall"
			>
				<Box as="span" display={{ base: 'inline', md: 'none' }}>
					{resolvedMobileDetail}
				</Box>
				<Box as="span" display={{ base: 'none', md: 'inline' }}>
					{resolvedDetail}
				</Box>
				{showViewRecords && (
					<InlineLinkButton ml={2} onClick={onViewRecords}>
						View records
					</InlineLinkButton>
				)}
			</Text>
		</Box>
	);
};

export default KnowledgeBaseBannerView;
